import React, { useEffect, useState } from "react";
import { Button, Modal, Pagination, Spin } from "antd";
import { GoBack, HeaderText, MainLayout, Checkbox, ExcelDownloadBtn, PdfDownloadBtn } from "@/components";
import "../../asbestos/tables/tables.scss";
import TrashIcon from "@/assets/images/trash-red-icon.svg";
import EditIcon from "@/assets/images/edit-blue-icon.svg";
import PDFIcon from "@/assets/images/pdf-file-icon.png";
import InputText from "./../../../components/form-group/inputText";
import { openNotificationWithIcon } from "../../../services/toast";
import { useLocation } from "react-router-dom";
import { ExclamationCircleFilled } from "@ant-design/icons";
import confirm from "antd/es/modal/confirm";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchEntries,
  updateEntry,
  deleteEntry,
  deleteEntries,
  changePage,
} from "../../../redux/slice/entriesSlice";
import { downloadFileTable, getFirstPathSegment } from "../../../services/Utils";

function EditEntries() {
  const location = useLocation();
  const buildingId = location.pathname.split("/").pop();
  const pathName = getFirstPathSegment(location?.pathname);
  const dispatch = useDispatch();
  const { rowData, isLoading, totalEntries, pageSize, currentPage } =
    useSelector((state) => state.entries);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [editRowData, setEditRowData] = useState({});
  const [changedData, setChangedData] = useState({});

  useEffect(() => {
    if (buildingId) {
      dispatch(fetchEntries(buildingId));
    }
  }, [buildingId, dispatch]);

  // entries.currentPage is shared by all four lead screens and was never reset,
  // so opening a second building after paging through the first issued
  // skip=<old offset> and rendered an empty table with no error.
  useEffect(() => {
    return () => {
      dispatch(changePage({ page: 1, pageSize: 10 }));
    };
  }, [dispatch]);

  const handlePageChange = (page, size) => {
    // Selections are page-scoped. Carrying them across pages meant the bulk
    // delete removed rows the user could no longer see, the per-row delete icon
    // silently disappeared, and Excel/PDF exports contained only the off-screen
    // selection.
    setSelectedRows([]);
    dispatch(changePage({ page, pageSize: size }));
    dispatch(fetchEntries(buildingId));
  };

  const handleEditClick = (index) => {
    // setEditRowIndex(index);
    // changedData is cleared here as well as on close: dismissing the modal with
    // Esc, the X, or a mask click left edits pending, and the next row opened
    // would save them onto that row instead.
    setChangedData({});
    setEditRowData({ ...rowData[index] });
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setChangedData({});
  };

  function handleChangeUpdate(e) {
    const { name, value } = e.target;
    const data = { [name]: value };
    setEditRowData((prev) => ({
      ...prev,
      ...data,
    }));

    setChangedData((prev) => ({
      ...prev,
      ...data,
    }));
  }

  function handleUpdate() {
    if (!Object.keys(changedData)?.length) {
      return openNotificationWithIcon(
        "error",
        "Please add some data to update"
      );
    }
    // A cleared number input yields "", which reached sql.Float server-side and
    // coerced to 0. For an XRF reading, 0 is a measurement -- not "no value" --
    // so send an explicit null instead.
    const payload = Object.fromEntries(
      Object.entries(changedData).map(([k, v]) => [k, v === "" ? null : v])
    );
    dispatch(
      updateEntry(
        payload,
        editRowData?.id,
        buildingId,
        currentPage,
        pageSize
      )
    );
    setIsEditModalOpen(false);
    setChangedData({});
  }

  const formatValue = (value) =>
    value !== "N/A" && (value || value === 0) ? value : "";

  const handleDeleteRow = (index) => {
    const rowId = rowData[index]?.id;
    if (!rowId) return;

    confirm({
      title: "Are you sure you want to delete this entry?",
      icon: <ExclamationCircleFilled />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      content: (
        <div style={{ position: "relative", top: "44px", left: "200px" }}>
          <Button
            onClick={() => {
              Modal.destroyAll();
            }}
          >
            {" "}
            No{" "}
          </Button>
        </div>
      ),
      onOk() {
        try {
          dispatch(deleteEntry(rowId, buildingId));
        } catch (error) {
          openNotificationWithIcon("error", "Failed to delete entry");
        }
      },
      onCancel() {
        console.log("Action cancelled");
      },
    });
  };

  const handleRowSelection = (id) => {
    setSelectedRows((prev) => {
      if (prev.includes(id)) {
        return prev.filter((rowId) => rowId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedRows.length === rowData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(rowData.map((data) => data.id));
    }
  };

  const handleDeleteAll = (selectedIds) => {
    confirm({
      title: "Are you sure you want to delete all selected entries?",
      icon: <ExclamationCircleFilled />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      content: (
        <div style={{ position: "relative", top: "44px", left: "200px" }}>
          <Button
            onClick={() => {
              Modal.destroyAll();
            }}
          >
            {" "}
            No{" "}
          </Button>
        </div>
      ),
      onOk() {
        try {
          dispatch(deleteEntries(selectedIds, buildingId));
          setSelectedRows([]);
        } catch (error) {
          openNotificationWithIcon("error", "Failed to delete entries");
        }
      },
      onCancel() {
        console.log("Action cancelled");
      },
    });
  };

  const handleDownloadPDF = async (typeStatus) => {
    const fileName = "Edit Entries";
    let type = typeStatus === "xlsx" ? "excel" : "pdf";
    const url = `v1/leadTable/results/${buildingId}/record`;
  
    const data = {
      format: type,
      tableName: "3",
      ...(selectedRows.length > 0 && { docIds: selectedRows }),
    };
  
    await downloadFileTable(typeStatus, url, fileName, data);
  };

  return (
    <MainLayout>
      <div className="table-one-wrapper tables-page-wrapper">
        <HeaderText>
          <GoBack />
          Edit Entries
        </HeaderText>
        {isLoading ? (
          <Spin delay={20} fullscreen size="large" />
        ) : (
          <div className="table-wrapper">
            <div>
                <div className="d-flex justify-content-end gap-2 mb-2 delImg">
                  <ExcelDownloadBtn onClick={() =>handleDownloadPDF("xlsx")} />
                  <PdfDownloadBtn onClick={() =>handleDownloadPDF("pdf")} />
                  {selectedRows.length > 1 && (
                    <img className="px-2 delImage" src={TrashIcon} alt="Delete" onClick={() => handleDeleteAll(selectedRows)}/>
                  )}
                </div>
            </div>
            <div
              className="table-overflow-wrapper container p-0"
              id="customScrollTable"
            >
              <table>
                <thead>
                  <tr>
                    <th>
                      <Checkbox
                        checked={rowData.length > 0 && selectedRows.length === rowData.length}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>Test</th>
                    <th>Serial</th>
                    <th>Pass/Fail</th>
                    <th>Unit Type</th>
                    <th>Calibration Model / Matrix ID</th>
                    <th>Display Sigma</th>
                    <th>LOD Sigma</th>
                    <th>Pb</th>
                    <th>Pb +/-</th>
                    <th>Pb P/F</th>
                    <th>Result</th>
                    <th>Lead (mg/cm²)</th>
                    <th>Mode</th>
                    <th>Job #</th>
                    <th>Wall</th>
                    <th>Structure</th>
                    <th>Location</th>
                    <th>Member</th>
                    <th>Paint Condition</th>
                    <th>Substrate</th>
                    <th>Color</th>
                    <th>Room Name</th>
                    <th>Building Type</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rowData.map((data, index) => (
                    <tr
                      key={data.id}
                      className={
                        selectedRows.includes(data.id) ? "selected-row" : ""
                      }
                    >
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        <Checkbox
                          checked={selectedRows.includes(data.id)}
                          onChange={() => handleRowSelection(data.id)}
                        />
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.TestNumber)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.SerialNumber)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.PassFail)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.UnitType)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.CalibrationModelMatrixID)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.DisplaySigma)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.LODSigma)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.Pb)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.PbPlusMinus)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.PbPassFail)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.Result)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.Lead_mg_cm2)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.Mode)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.Job_Number)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.Wall)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.Structure)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.Location)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.Member)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.Paint_Condition)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.Substrate)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.Color)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.Room_Name)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        {formatValue(data.Building_Type)}
                      </td>
                      <td
                        className={
                          selectedRows.includes(data.id) ? "selected-row" : ""
                        }
                      >
                        <div className="action-btn gap-4 d-flex justify-content-center">
                          <img
                            className="edit-icon"
                            src={EditIcon}
                            alt="Edit"
                            onClick={() => handleEditClick(index)}
                            style={{ cursor: "pointer" }}
                          />
                          {selectedRows.length <= 1 && (<img
                            className="trash-icon"
                            src={TrashIcon}
                            alt="Delete"
                            onClick={() => handleDeleteRow(index)}
                            style={{ cursor: "pointer" }}
                          />)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {isEditModalOpen && (
        <Modal
          title="Edit Entry"
          centered
          open={isEditModalOpen}
          onCancel={closeEditModal}
          onOk={handleUpdate}
          okText="Save"
          width={1000}
          footer={(_, { OkBtn }) => (
            <>
              <OkBtn />
            </>
          )}
        >
          <div className="edit-form">
            <div className="row">
              <InputText
                name="TestNumber"
                label="Test Number"
                className="mb-3 w-25"
                type="number"
                value={formatValue(editRowData?.TestNumber)}
                onChange={handleChangeUpdate}
              />
              <InputText
                name="SerialNumber"
                label="Serial Number"
                className="mb-3 w-25"
                value={formatValue(editRowData?.SerialNumber)}
                onChange={handleChangeUpdate}
              />
              <InputText
                name="PassFail"
                label="Pass/Fail"
                className="mb-3 w-25"
                value={formatValue(editRowData?.PassFail)}
                onChange={handleChangeUpdate}
              />
              <InputText
                name="UnitType"
                label="Unit Type"
                className="mb-3 w-25"
                value={formatValue(editRowData?.UnitType)}
                onChange={handleChangeUpdate}
              />
            </div>

            <div className="row">
              <InputText
                name="CalibrationModelMatrixID"
                label="Calibration Model / Matrix ID"
                className="mb-3 w-25"
                value={formatValue(editRowData?.CalibrationModelMatrixID)}
                onChange={handleChangeUpdate}
              />
              <InputText
                name="DisplaySigma"
                label="Display Sigma"
                className="mb-3 w-25"
                type="number"
                value={formatValue(editRowData?.DisplaySigma)}
                onChange={handleChangeUpdate}
              />
              <InputText
                name="LODSigma"
                label="LOD Sigma"
                className="mb-3 w-25"
                type="number"
                value={formatValue(editRowData?.LODSigma)}
                onChange={handleChangeUpdate}
              />
              <InputText
                name="Pb"
                label="Pb"
                className="mb-3 w-25"
                type="number"
                value={formatValue(editRowData?.Pb)}
                onChange={handleChangeUpdate}
              />
            </div>

            <div className="row">
              <InputText
                name="PbPlusMinus"
                label="Pb +/-"
                className="mb-3 w-25"
                type="number"
                value={formatValue(editRowData?.PbPlusMinus)}
                onChange={handleChangeUpdate}
              />
              <InputText
                name="PbPassFail"
                label="Pb P/F"
                className="mb-3 w-25"
                value={formatValue(editRowData?.PbPassFail)}
                onChange={handleChangeUpdate}
              />
              <InputText
                name="Result"
                label="Result"
                className="mb-3 w-25"
                value={formatValue(editRowData?.Result)}
                onChange={handleChangeUpdate}
              />
              <InputText
                name="Lead_mg_cm2"
                label="Lead"
                className="mb-3 w-25"
                // LEAD_RESULTS.Lead_mg_cm2 is decimal(10,2) in the database, so this
                // input must stay numeric. (Job_Number, below, is varchar and is not.)
                type="number"
                value={formatValue(editRowData?.Lead_mg_cm2)}
                onChange={handleChangeUpdate}
              />
            </div>

            <div className="row">
              <InputText
                name="Mode"
                label="Mode"
                className="mb-3 w-25"
                value={formatValue(editRowData?.Mode)}
                onChange={handleChangeUpdate}
              />
              <InputText
                name="Job_Number"
                label="Job Number"
                className="mb-3 w-25"
                value={formatValue(editRowData?.Job_Number)}
                onChange={handleChangeUpdate}
              />
              <InputText
                name="Wall"
                label="Wall"
                className="mb-3 w-25"
                value={formatValue(editRowData?.Wall)}
                onChange={handleChangeUpdate}
              />
              <InputText
                name="Structure"
                label="Structure"
                className="mb-3 w-25"
                value={formatValue(editRowData?.Structure)}
                onChange={handleChangeUpdate}
              />
            </div>

            <div className="row">
              <InputText
                name="Location"
                label="Location"
                className="mb-3 w-25"
                value={formatValue(editRowData?.Location)}
                onChange={handleChangeUpdate}
              />
              <InputText
                name="Member"
                label="Member"
                className="mb-3 w-25"
                value={formatValue(editRowData?.Member)}
                onChange={handleChangeUpdate}
              />
              <InputText
                name="Paint_Condition"
                label="Paint Condition"
                className="mb-3 w-25"
                value={formatValue(editRowData?.Paint_Condition)}
                onChange={handleChangeUpdate}
              />
              <InputText
                name="Substrate"
                label="Substrate"
                className="mb-3 w-25"
                value={formatValue(editRowData?.Substrate)}
                onChange={handleChangeUpdate}
              />
            </div>

            <div className="row">
              <InputText
                name="Color"
                label="Color"
                className="mb-3 w-25"
                value={formatValue(editRowData?.Color)}
                onChange={handleChangeUpdate}
              />
              <InputText
                name="Room_Name"
                label="Room Name"
                className="mb-3 w-25"
                value={formatValue(editRowData?.Room_Name)}
                onChange={handleChangeUpdate}
              />
              <InputText
                name="Building_Type"
                label="Building Type"
                className="w-25"
                value={formatValue(editRowData?.Building_Type)}
                onChange={handleChangeUpdate}
              />
            </div>
          </div>
        </Modal>
      )}
      {!isLoading && totalEntries > 0 && totalEntries > 10 && (
        <div className="pagination">
          <Pagination
            defaultCurrent={1}
            defaultPageSize={10}
            current={currentPage}
            total={totalEntries}
            pageSize={pageSize}
            onChange={handlePageChange}
            showSizeChanger
          />
        </div>
      )}
    </MainLayout>
  );
}

export default EditEntries;
